import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowUpward } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import imgLogin from 'src/assets/images/help/getting-started/login-in.png';
import imgUploadButton from 'src/assets/images/help/invoice-upload/01-upload-invoice.svg';
import imgUploadSection from 'src/assets/images/help/invoice-upload/02-upload-section.svg';
import imgUploadResult from 'src/assets/images/help/invoice-upload/03-upload-result.svg';
import imgUploadGrouped from 'src/assets/images/help/invoice-upload/04-upload-result.png';
import imgNotificationPanel from 'src/assets/images/help/invoice-upload/05-notification.png';
import imgToast from 'src/assets/images/help/invoice-upload/06-toast.svg';
import imgAudit01 from 'src/assets/images/help/review-audit-exception/01-audit-exceptions.svg';
import imgAudit02 from 'src/assets/images/help/review-audit-exception/02-audit-exceptions.svg';
import imgAudit03 from 'src/assets/images/help/review-audit-exception/03-audit-exceptions.svg';
import imgAudit04 from 'src/assets/images/help/review-audit-exception/04-audit-exceptions.svg';
import imgAudit05 from 'src/assets/images/help/review-audit-exception/05-audit-exceptions.png';
import imgAudit06 from 'src/assets/images/help/review-audit-exception/06-audit-exceptions.png';
import imgAudit07 from 'src/assets/images/help/review-audit-exception/07-audit-exceptions.svg';
import imgAudit08 from 'src/assets/images/help/review-audit-exception/08-audit-exceptions.svg';
import imgAudit09 from 'src/assets/images/help/review-audit-exception/09-audit-exceptions.svg';
import imgAudit10 from 'src/assets/images/help/review-audit-exception/10-audit-exceptions.svg';
import imgInvoiceIssue01 from 'src/assets/images/help/resolving-invoice-issue/01-Resolving-invoice.svg';
import imgInvoiceIssue02 from 'src/assets/images/help/resolving-invoice-issue/02-Resolving-invoice.svg';
import imgInvoiceIssue03 from 'src/assets/images/help/resolving-invoice-issue/03-Resolving-invoice.svg';
import imgInvoiceIssue04 from 'src/assets/images/help/resolving-invoice-issue/04-Resolving-invoice.svg';
import imgNRCF01 from 'src/assets/images/help/handling-nrcf/01-nrcf.png';
import imgNRCF02 from 'src/assets/images/help/handling-nrcf/02-nrcf.svg';
import imgNRCF03 from 'src/assets/images/help/handling-nrcf/03-nrcf.svg';
import imgNRCF04 from 'src/assets/images/help/handling-nrcf/04-nrcf.svg';
import imgNRCF05 from 'src/assets/images/help/handling-nrcf/05-nrcf.png';
import imgNRCF06 from 'src/assets/images/help/handling-nrcf/06-nrcf.png';
import imgNRCF07 from 'src/assets/images/help/handling-nrcf/07-nrcf.png';
import imgNRCF08 from 'src/assets/images/help/handling-nrcf/08-nrcf.svg';
import imgNRCF09 from 'src/assets/images/help/handling-nrcf/09-nrcf.png';
import imgNRCF10 from 'src/assets/images/help/handling-nrcf/10-nrcf.png';
import imgReport01 from 'src/assets/images/help/report-analytic/01-report.svg';
import imgReport02 from 'src/assets/images/help/report-analytic/02-report.svg';
import imgReport03 from 'src/assets/images/help/report-analytic/03-report.png';
import imgReport04 from 'src/assets/images/help/report-analytic/04-report.svg';
import imgReport05 from 'src/assets/images/help/report-analytic/05-report.svg';
import imgReport06 from 'src/assets/images/help/report-analytic/06-report.svg';
import imgReport07 from 'src/assets/images/help/report-analytic/07-report.svg';
import imgReport08 from 'src/assets/images/help/report-analytic/08-report.svg';
import imgRCM01 from 'src/assets/images/help/ratecard-manager/01-rcm.svg';
import imgRCM02 from 'src/assets/images/help/ratecard-manager/02-rcm.svg';
import imgRCM03 from 'src/assets/images/help/ratecard-manager/03-rcm.svg';
import imgRCM04 from 'src/assets/images/help/ratecard-manager/04-rcm.png';
import imgRCM05 from 'src/assets/images/help/ratecard-manager/05-rcm.png';
import imgRCM06 from 'src/assets/images/help/ratecard-manager/06-rcm.svg';
import imgRCM07 from 'src/assets/images/help/ratecard-manager/07-rcm.svg';
import imgRCM08 from 'src/assets/images/help/ratecard-manager/08-rcm.svg';
import imgRCM09 from 'src/assets/images/help/ratecard-manager/09-rcm.png';

// Helper to find the closest scrollable parent
const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  if (!element) return null;

  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

interface HelpContentProps {
  activeTopic?: string;
}

const GettingStartedContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Getting Started in FREYA</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>What is FREYA?</h4>
    <p className='text-grey-10 mb-20 leading-relaxed'>
      FREYA is a Freight Invoice Audit &amp; Analytics Platform designed to help organizations validate, audit, and resolve freight invoices against configured
      rate cards — ensuring accuracy before payment.
    </p>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Who Uses FREYA?</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      FREYA supports multiple user roles. Depending on your organization's configuration, some or all of the following roles may be active:
    </p>
    <div className='overflow-x-auto mb-16'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Role</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Responsibility</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Procurement', 'Handles unknown charges and No Rate Card Found (NRCF) exceptions'],
            ['Logistics (Operation)', 'Handles unit price, quantity, and double charge deviations'],
            ['Supply Chain', 'Handles all shipment-level deviations, unknown charges, NRCF resolution, and charge approvals (where configured)'],
            ['Finance', 'Reviews and approves/rejects high-value deviations escalated from other roles (where configured)'],
            ['Administrator', 'Manages users, entities, service providers, exchange rates, and platform configurations'],
            ['Internal Team', 'Monitors and resolves invoice ingestion or recognition issues via the Internal Dashboard'],
          ].map(([role, responsibility]) => (
            <tr key={role} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{role}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{responsibility}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-24'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        <strong>⚠️ Note:</strong> The roles available to you and the audit workflow you follow depend on your organization's platform configuration. Some roles
        may not apply to your setup.
      </p>
    </div>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Logging In</h4>
    <img src={imgLogin} alt='FREYA login screen' className='my-30 block w-[400px]' />
    <p className='text-grey-10 mb-12 leading-relaxed'>FREYA supports two login methods depending on your organization's setup:</p>
    <div className='mb-12'>
      <p className='font-medium mb-8'>Standard Login (Email &amp; Password)</p>
      <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-20'>
        <li>Navigate to the FREYA login page.</li>
        <li>
          Enter your registered <strong>Email</strong> and <strong>Password</strong>.
        </li>
        <li>
          Click <strong>Log In</strong>.
        </li>
      </ol>
    </div>
    <h4 className='font-medium mb-8 mb-12'>Single Sign-On (SSO)</h4>
    <p className='text-grey-10 mb-20 leading-relaxed'>
      If your organization has SSO enabled, click the <strong>SSO login</strong> option on the login page and authenticate through your organization's identity
      provider. SSO is configured by your organization's administrator — contact your admin if you need SSO access set up or modified.
    </p>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Resetting Your Password</h4>
    <p className='text-grey-10 mb-8 leading-relaxed'>If you've forgotten your password:</p>
    <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>
        Click <strong>Forgot Password?</strong> on the login page.
      </li>
      <li>Enter your registered email address.</li>
      <li>
        Click <strong>Send Reset Link</strong>.
      </li>
      <li>Check your email inbox for a password reset link.</li>
      <li>Click the link and follow the instructions to set a new password.</li>
    </ol>
    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-24'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        <strong>⚠️ Note:</strong> The reset link expires after a limited time. If it has expired, repeat the process to request a new one. If you do not receive
        the email, check your spam/junk folder.
      </p>
    </div>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Navigation Overview</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      The main navigation is located on the <strong>left sidebar</strong> and provides access to all core modules:
    </p>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Module</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Audit Exceptions', 'Review and resolve flagged invoices'],
            ['Performance Assessment', 'View savings and performance metrics across service providers'],
            ['Reports & Analytics', 'Generate and export financial and operational reports'],
            ['Rate Card Manager', 'Configure contractual rates per service provider'],
            ['Settings', 'Manage users, entities, providers, and platform settings'],
            ['Internal Dashboard', 'Internal monitoring and issue resolution (internal team only)'],
          ].map(([module, description]) => (
            <tr key={module} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{module}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

const InvoiceUploadContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Uploading Invoices</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>What is Invoice Upload?</h4>
    <p className='text-grey-10 mb-20 leading-relaxed'>
      Invoice Upload is how you get invoices into FREYA for processing. Once uploaded, FREYA automatically reads each invoice, extracts the relevant data
      (charges, amounts, shipment details), identifies the service provider, and compares everything against your rate cards. Any discrepancies are flagged for
      your review.
    </p>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>What happens when you upload:</h4>
    <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>FREYA receives your PDF file(s)</li>
      <li>The system reads and extracts all invoice data using OCR technology</li>
      <li>Each invoice is matched to a service provider and shipment</li>
      <li>Charges are compared against your configured rate cards</li>
      <li>Results appear in Audit Exceptions (if issues found) or Reports (if clean)</li>
    </ol>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Two Ways to Upload</h4>

    <p className='font-medium text-neutral-80 mb-8'>
      Option 1: Manual Upload <span className='font-normal text-grey-10'>(Recommended for immediate processing)</span>
    </p>
    <p className='text-grey-10 mb-12 leading-relaxed'>Use this when you have invoices on hand and want to process them right away.</p>
    <ol className='list-decimal list-inside space-y-16 text-grey-10 pl-4 mb-24'>
      <li>
        Click <strong>Upload New Invoices</strong> button at the top of the screen
        <br />
        <img src={imgUploadButton} alt='Upload New Invoices button' className='mt-12 block' />
      </li>
      <li>
        Drag and drop your PDF files into the upload area, or click to browse your computer
        <br />
        <img src={imgUploadSection} alt='Invoice upload drag and drop area' className='mt-12 block w-[550px]' />
      </li>
      <li>You'll see each file listed with its name and size</li>
      <li>
        Click <strong>Upload</strong> to start processing
      </li>
      <li>FREYA automatically detects which service provider the invoice belongs to</li>
      <li>
        Watch the real-time progress as each invoice is processed
        <br />
        <img src={imgUploadResult} alt='Invoice processing progress' className='mt-12 block w-[550px]' />
      </li>
    </ol>

    <p className='font-medium text-neutral-80 mb-8'>
      Option 2: Email Upload <span className='font-normal text-grey-10'>(Best for automated workflows)</span>
    </p>
    <p className='text-grey-10 mb-12 leading-relaxed'>Use this for hands-off processing – invoices are handled automatically as they arrive.</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>Your organization has a dedicated FREYA email address</li>
      <li>Forward invoices to this address or have vendors send directly</li>
      <li>FREYA processes them automatically in the background</li>
      <li>Check the Notifications panel to see results</li>
    </ul>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Understanding the Notifications Panel</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      The <strong>Notifications panel</strong> (right side of screen) is your upload history for the last 30 days. It shows you what's been uploaded and the
      current status of each batch.
    </p>
    <img src={imgNotificationPanel} alt='Notifications panel' className='mb-20 block  w-[550px]' />

    <p className='font-medium text-neutral-80 mb-8'>How invoices are grouped:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>
        <strong>By Service Provider</strong> – Successfully recognized invoices are grouped under their provider name (e.g., "DHL Express – 3 invoices")
      </li>
      <li>
        <strong>Under Review</strong> – Invoices that couldn't be automatically matched are grouped here for manual review
      </li>
    </ul>
    <img src={imgUploadGrouped} alt='Invoices grouped by service provider' className='mb-20 block  w-[550px]' />

    <p className='font-medium text-neutral-80 mb-8'>What the status labels mean:</p>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Status</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>What It Means</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>What to Do</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Uploading', 'File is being transferred to FREYA', 'Wait for completion'],
            ['Processing', 'FREYA is reading and analyzing the invoice', 'Wait for completion'],
            ['Flagged', 'Issues found that need your attention', 'Go to Audit Exceptions to review'],
            ['Ready for Payment', 'Invoice passed all checks – no issues', 'Approve for payment'],
            ['Rejected', 'Invoice has been rejected during review', 'Check Activity Log for details'],
            ['Duplicate', 'This exact invoice already exists', 'Click "See Existing Invoice" to view the original'],
            ['Partially Processed', "Some data couldn't be extracted", 'Click "See File" to review what\'s missing'],
            ['Under Review', "FREYA couldn't identify the provider", 'Internal team is investigating'],
          ].map(([status, meaning, action]) => (
            <tr key={status} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{status}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{meaning}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>Batch status labels (for multiple invoices):</p>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Batch Status</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>What It Means</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Completed', 'All invoices processed successfully'],
            ['Completed with Warnings', 'All processed, but some have issues to review'],
            ['Partially Completed', 'Some invoices recognized, others need review'],
            ['Processing', 'Still working on the batch'],
          ].map(([batchStatus, meaning]) => (
            <tr key={batchStatus} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{batchStatus}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{meaning}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Staying Updated with Toast Notifications</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>If you close the upload window while processing continues, FREYA keeps you informed:</p>
    <img src={imgToast} alt='Toast notifications' className='mb-16 block  w-[550px]' />
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>
        <strong>"X invoices being processed"</strong> – Processing is ongoing. This notification stays visible until you click it. Click{' '}
        <strong>Show Details</strong> to reopen the progress view.
      </li>
      <li>
        <strong>"Processing completed"</strong> – All done! Click <strong>Show Details</strong> to see the results summary.
      </li>
    </ul>
  </>
);

const ReviewingInvoicesContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Reviewing Audit Exceptions</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>What are Audit Exceptions?</h4>
    <p className='text-grey-10 mb-16 leading-relaxed'>
      Audit Exceptions are invoices that FREYA has flagged because something doesn't match your contracted rates. Think of it as FREYA saying "Hey, I found
      something you should look at before approving this payment."
    </p>

    <p className='font-medium text-neutral-80 mb-8'>Why invoices get flagged:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>A charge is higher than your contracted rate</li>
      <li>There's a charge that doesn't exist in your rate card</li>
      <li>The quantity billed is more than expected</li>
      <li>The same charge appears twice</li>
      <li>No matching rate card was found for the shipment</li>
    </ul>
    <p className='text-grey-10 mb-24 leading-relaxed'>
      <strong>Your job in Audit Exceptions:</strong> Review each flagged item, investigate if needed, and make a decision – accept the charge (with
      justification) or reject it for dispute with the vendor.
    </p>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>How to Access Audit Exceptions</h4>
    <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>
        Click <strong>Audit Exceptions</strong> in the left sidebar
      </li>
      <li>You'll see a list of service providers with flagged invoices</li>
      <li>Each provider card shows the number of pending invoices and last update time</li>
      <li>
        Click on a <strong>Service Provider</strong> to see their flagged invoices
      </li>
    </ol>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Understanding the Dashboard</h4>
    <img src={imgAudit01} alt='Summary metrics bar' className='mb-20 block' />

    <p className='font-medium text-neutral-80 mb-8'>Summary Metrics (Top of Page)</p>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      At the top, you'll see key numbers that give you a quick health check. You can customize which metrics appear:
    </p>
    <ol className='list-decimal list-inside space-y-16 text-grey-10 pl-4 mb-24'>
      <li>
        Click <strong>Edit Metrics</strong> button
        <br />
        <img src={imgAudit02} alt='Edit metrics button' className='mt-12 block' />
      </li>
      <li>
        Choose up to 4 metrics from the available options:
        <br />
        <img src={imgAudit03} alt='Metrics selection panel' className='mt-12 block' />
        <ul className='list-disc list-inside space-y-2 mt-8 pl-4'>
          <li>NRCF Shipments (invoices without matching rate cards)</li>
          <li>Unit Price Deviations</li>
          <li>Quantity Deviations</li>
          <li>Unknown Charges</li>
          <li>Approvals Pending</li>
          <li>Total Net Deviations</li>
          <li>Total Flagged Items</li>
        </ul>
      </li>
      <li>
        Click <strong>Apply</strong> to save your selection
      </li>
      <li>
        Click <strong>Reset</strong> anytime to restore defaults
      </li>
    </ol>
    <p className='text-grey-10 mb-24 leading-relaxed'>These metrics update automatically based on your filters.</p>

    <p className='font-medium text-neutral-80 mb-12'>Two Tabs to Organize Your Work:</p>
    <img src={imgAudit04} alt='Action Needed and All Exceptions tabs' className='mb-16 block' />
    <div className='overflow-x-auto mb-16'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Tab</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>What It Shows</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Who It's For</th>
          </tr>
        </thead>
        <tbody>
          <tr className='even:bg-neutral-5'>
            <td className='border border-neutral-20 px-12 py-8 font-medium'>Action Needed</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>Only invoices that require YOUR action based on your role</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>You – these are your assignments</td>
          </tr>
          <tr className='even:bg-neutral-5'>
            <td className='border border-neutral-20 px-12 py-8 font-medium'>All Exceptions</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>Every flagged invoice for this provider</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>Anyone who wants the full picture</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-16'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        <strong>💡 See an eye icon?</strong> That invoice is assigned to someone in a different role. You can view the details but can't take action on it.
      </p>
    </div>
    <img src={imgAudit05} alt='Eye icon tooltip showing invoice assignments' className='mb-24 block' />

    <p className='font-medium text-neutral-80 mb-8'>What Each Invoice Row Shows:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>Invoice number and date</li>
      <li>Pickup date</li>
      <li>Shipment info (single shipment number, or "4 Shipments" if multiple)</li>
      <li>Trade lane (origin → destination)</li>
      <li>Total amount</li>
      <li>Status (Flagged)</li>
      <li>
        Pending items (e.g., "3 Deviations, 2 Approvals" – hover over the <strong>"i"</strong> icon for details)
      </li>
    </ul>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Finding Specific Invoices</h4>
    <p className='font-medium text-neutral-80 mb-8'>Quick Search</p>
    <p className='text-grey-10 mb-12 leading-relaxed'>The search bar above the table lets you find invoices instantly:</p>
    <img src={imgAudit06} alt='Quick search results' className='mb-12 block w-[520px] border border-neutral-20' />
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>
        Type an <strong>invoice number</strong> to find a specific invoice
      </li>
      <li>
        Type a <strong>shipment number</strong> to find all invoices containing that shipment
      </li>
      <li>Results appear as you type (autocomplete)</li>
    </ul>

    <p className='font-medium text-neutral-80 mb-8'>Ready for Payment Toggle</p>
    <p className='text-grey-10 mb-12 leading-relaxed'>Turn this on to show only invoices that have been fully resolved and are ready for payment approval.</p>
    <img src={imgAudit07} alt='Only Show Approved Invoice toggle' className='mb-24 block border border-b-0 border-neutral-20 w-[520px]' />

    <p className='font-medium text-neutral-80 mb-8'>Column Filters</p>
    <p className='text-grey-10 mb-12 leading-relaxed'>Each column has its own filter. Click the filter icon in any column header to:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-12'>
      <li>See all unique values in that column</li>
      <li>Check/uncheck specific values to show or hide</li>
      <li>Search within the filter list</li>
      <li>Sort A→Z or Z→A</li>
    </ul>
    <img src={imgAudit08} alt='Column filter popup' className='mb-12 block' />
    <p className='text-grey-10 mb-12 leading-relaxed'>
      When a column filter is active, you'll see a green icon and a counter (like <strong>2/5</strong> meaning "2 of 5 values selected").
    </p>
    <div className='bg-amber-50 border-l-4 border-primary rounded-4 p-16 mb-24'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        <strong>⚠️ Note:</strong> Column filters only affect the current tab. They don't carry over when you switch between Action Needed and All Exceptions.
      </p>
    </div>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Customizing Your Table View</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      Make the table work for you by clicking on <strong>Column</strong> button:
    </p>
    <img src={imgAudit09} alt='Columns button' className='mb-16 block' />

    <p className='font-medium text-neutral-80 mb-8'>Show or Hide Columns</p>
    <img src={imgAudit10} alt='Adjust Displayed Columns panel' className='mb-12 block w-[520px]' />
    <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>Click the column settings icon</li>
      <li>Check or uncheck columns to show or hide them</li>
      <li>Some columns are "locked" and always visible (like Invoice Number)</li>
    </ol>
    <p className='text-grey-10 mb-4 leading-relaxed'>
      <strong>Reorder Columns</strong> — Drag and drop column headers to arrange them in your preferred order.
    </p>
    <p className='text-grey-10 mb-24 leading-relaxed'>
      <strong>Reset to Default</strong> — Click Reset to restore the original column layout.
    </p>
  </>
);

const AuditWorkflowsContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Audit Workflows</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Overview</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      FREYA supports multiple audit workflow configurations to accommodate different organizational structures. The workflow determines which roles are
      involved, what deviation types they handle, and how escalation works.
    </p>
    <p className='text-grey-10 mb-24 leading-relaxed'>
      Your organization's workflow is configured by the platform administrator. Below are the supported workflow models.
    </p>

    {/* ── Workflow A ── */}
    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Workflow A – Procurement &amp; Logistics</h4>
    <p className='text-grey-10 mb-16 leading-relaxed'>
      This workflow splits audit responsibility between <strong>Procurement</strong> and <strong>Logistics (Operation)</strong>.
    </p>

    <p className='font-medium text-neutral-80 mb-8'>Role Assignments:</p>
    <div className='overflow-x-auto mb-20'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Role</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Handles</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='border border-neutral-20 px-12 py-8 font-medium'>Procurement</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>Unknown Charges, NRCF (No Rate Card Found)</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-12 py-8 font-medium'>Logistics (Operation)</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>Unit Price deviations, Quantity deviations, Double Charges</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>Flow:</p>
    <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-20'>
      <li>Invoices are flagged during ingestion and deviations are assigned to the appropriate role.</li>
      <li>Each role resolves their assigned deviations independently.</li>
      <li>For multi-shipment invoices, each shipment must be confirmed individually.</li>
      <li>Once all shipments are resolved and confirmed, the invoice outcome is determined (Ready for Payment, Rejected, or Dispute).</li>
    </ol>

    <p className='font-medium text-neutral-80 mb-8'>Decisions – Procurement (Unknown Charges &amp; NRCF):</p>
    <div className='overflow-x-auto mb-20'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Decision</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Yes, as one-off', 'Accepted for this invoice only. Reason required.'],
            ['Yes, as recurring', 'Accepted for a defined period. Applies to future invoices.'],
            ['Yes, add mapping', 'Mapped to Rate Card. Triggers recalculation.'],
          ].map(([decision, outcome]) => (
            <tr key={decision} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{decision}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>Decisions – Logistics (Unit Price, Quantity, Double Charge):</p>
    <div className='overflow-x-auto mb-20'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Decision</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Yes (accept)', 'Approved with justification.'],
            ['No (reject)', 'Considered in the final invoice outcome.'],
          ].map(([decision, outcome]) => (
            <tr key={decision} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{decision}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>NRCF Resolution (Procurement):</p>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Option</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Setup New Service', 'Create a new rate card service for the shipment'],
            ['Accept as Spot Shipment', 'One-time acceptance; charges not saved to Rate Card Manager'],
            ['Reject Shipment', 'Reject all charges with a reason'],
          ].map(([option, description]) => (
            <tr key={option} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{option}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* ── Workflow B ── */}
    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Workflow B – Supply Chain &amp; Finance</h4>
    <p className='text-grey-10 mb-16 leading-relaxed'>
      This workflow assigns primary audit responsibility to <strong>Supply Chain</strong>, with <strong>Finance</strong> providing secondary review for
      high-value deviations.
    </p>

    <p className='font-medium text-neutral-80 mb-8'>Role Assignments:</p>
    <div className='overflow-x-auto mb-12'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Role</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Handles</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='border border-neutral-20 px-12 py-8 font-medium'>Supply Chain</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>
              All flagged shipments, unknown charges, NRCF resolution, charge approvals (for accessorial charges), and all deviation types
            </td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-12 py-8 font-medium'>Finance</td>
            <td className='border border-neutral-20 px-12 py-8 text-grey-10'>
              Reviews deviations accepted by Supply Chain when the deviation amount exceeds the configured threshold (e.g., €500)
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-20'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        <strong>⚠️ Note:</strong> Procurement does not play a role in this workflow.
      </p>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>Flow:</p>
    <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-20'>
      <li>
        All flagged invoices and shipments are assigned to <strong>Supply Chain</strong>.
      </li>
      <li>Supply Chain resolves deviations (unknown charges, unit price, quantity, etc.).</li>
      <li>
        If Supply Chain accepts a deviation (as one-off, recurring, or spot) where the deviation amount exceeds the configured threshold, the shipment is
        escalated to <strong>Finance</strong> for review.
      </li>
      <li>A notification email is sent to Finance when a shipment requires their review.</li>
      <li>
        Finance reviews and either <strong>accepts</strong> or <strong>rejects</strong> the deviation.
      </li>
      <li>If Finance accepts → the deviation is approved, and the invoice can proceed to Ready for Payment (if nothing else is pending).</li>
      <li>If Finance rejects → the shipment is reassigned to Supply Chain for further action or dispute.</li>
    </ol>

    <p className='font-medium text-neutral-80 mb-8'>Decisions – Supply Chain:</p>
    <div className='overflow-x-auto mb-20'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Decision</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Yes, as one-off', 'Accepted for this invoice only. Reason required. If above threshold → escalated to Finance.'],
            ['Yes, as recurring', 'Accepted for a defined period. If above threshold → escalated to Finance.'],
            ['Yes, add mapping', 'Mapped to Rate Card. Triggers recalculation. Confirm button displayed after resolution.'],
            ['No, I reject', 'Rejected.'],
          ].map(([decision, outcome]) => (
            <tr key={decision} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{decision}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className='bg-amber-50 border-l-4 border-primary rounded-4 p-16 mb-20'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        <strong>Finance Escalation Trigger:</strong> Before saving a questionnaire with a deviation above the threshold, a message is displayed:{' '}
        <em>"This deviation will be reviewed by Finance."</em> The <strong>Confirm</strong> button assigns the shipment to Finance for review.
      </p>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>Decisions – Finance:</p>
    <div className='overflow-x-auto mb-20'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Decision</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Accept', 'Deviation approved. Confirm or Ready for Payment button displayed (if nothing else is pending).'],
            ['Reject', 'Shipment reassigned to Supply Chain.'],
          ].map(([decision, outcome]) => (
            <tr key={decision} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{decision}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{outcome}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>NRCF Resolution (Supply Chain):</p>
    <div className='overflow-x-auto mb-20'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Option</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Accept as Spot Shipment', 'One-time acceptance; charges categorized by Supply Chain'],
            ['Reject Shipment', 'Reject all charges with a reason'],
          ].map(([option, description]) => (
            <tr key={option} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{option}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>When the Confirm Button Appears (Supply Chain):</p>
    <p className='text-grey-10 mb-8 leading-relaxed'>The Confirm button is displayed when:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>
        An unknown charge has been mapped to the rate card, <strong>and/or</strong>
      </li>
      <li>
        A deviation above the configured threshold has been accepted as one-off or recurring, <strong>and/or</strong>
      </li>
      <li>There are other shipments still pending resolution.</li>
    </ul>

    <p className='text-grey-10 mb-16 leading-relaxed'>
      <strong>Approvals:</strong> Accessorial charge approvals are handled by Supply Chain only in this workflow.
    </p>

    <p className='font-medium text-neutral-80 mb-8'>Invoice Reassigned to Supply Chain (after Finance Rejection):</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>
        The <strong>Start Dispute</strong> button is enabled (provided no shipments are still under Finance review).
      </li>
      <li>
        Deviations rejected by Finance are <strong>not editable</strong> by Supply Chain.
      </li>
      <li>Other deviations previously reviewed by Supply Chain can still be edited.</li>
    </ul>

    {/* ── Workflow Comparison ── */}
    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Workflow Comparison</h4>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Feature</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Workflow A (Procurement &amp; Logistics)</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Workflow B (Supply Chain &amp; Finance)</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Primary reviewers', 'Procurement + Logistics', 'Supply Chain'],
            ['Secondary reviewer', '—', 'Finance (threshold-based)'],
            ['NRCF resolution', 'Procurement (all 3 options)', 'Supply Chain (Spot Shipment or Reject only)'],
            ['Unknown charge mapping', 'Procurement', 'Supply Chain'],
            ['Finance escalation', 'Not applicable', 'Automatic for deviations above threshold'],
            ['Dispute initiation', 'Either role (after all resolved)', 'Supply Chain only'],
            ['Accessorial approvals', 'Per role assignment', 'Supply Chain only'],
          ].map(([feature, workflowA, workflowB]) => (
            <tr key={feature} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{feature}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{workflowA}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{workflowB}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

const ResolvingInvoiceIssuesContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Resolving Invoice Issues (Action Details)</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Overview</h4>
    <p className='text-grey-10 mb-24 leading-relaxed'>
      The Action Details page is accessed by clicking into a flagged invoice from the Audit Exceptions list. It shows shipment-level deviations and allows users
      to resolve them based on their role within the configured audit workflow.
    </p>

    <h4 className='text-18 font-medium text-neutral-80 mb-12'>How to Access</h4>
    <p className='text-grey-10 mb-24 leading-relaxed font-medium'>Sidebar → Audit Exceptions → Select a flagged invoice → Action Details</p>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Page Layout</h4>

    <p className='font-medium text-neutral-80 mb-8'>Header</p>
    <img src={imgInvoiceIssue01} alt='Action Details header' className='mb-16 block' />
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>Service Provider name, Invoice Number, and current Status.</li>
      <li>Shipment Dropdown (for invoices with multiple shipments).</li>
    </ul>

    <p className='font-medium text-neutral-80 mb-8'>Tabs</p>
    <img src={imgInvoiceIssue02} alt='Invoice Details, Shipment Overview and Leg Details tabs' className='mb-16 block w-[520px]' />
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>
        <strong>Invoice Details</strong> – Dates, totals, and billed party information.
      </li>
      <li>
        <strong>Shipment Overview</strong> – Weight, packages, CO₂e, Incoterms, shipper/consignee.
      </li>
      <li>
        <strong>Leg Details</strong> – Multi-leg breakdown including ETD/ETA and linked invoices.
      </li>
    </ul>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Shipment Dropdown (Multi-Shipment Invoices)</h4>
    <img src={imgInvoiceIssue03} alt='Shipment dropdown grouped by status' className='mb-16 block' />
    <p className='text-grey-10 mb-12 leading-relaxed'>Shipments are grouped by status:</p>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Group</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Action Needed', 'Shipments awaiting your review'],
            ['Pending / Resolved', 'Shipments reviewed and confirmed'],
            ['Under Review', 'Shipments locked, escalated to support, or under Finance review'],
            ['Processing', 'Shipments still validating in the system'],
          ].map(([group, description]) => (
            <tr key={group} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{group}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Confirming Shipments</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      <strong>Single-Shipment Invoices</strong> — Decisions are applied immediately. No Confirm button is required.
    </p>
    <p className='text-grey-10 mb-16 leading-relaxed'>
      <strong>Multi-Shipment Invoices</strong> — Each shipment must be confirmed individually using the <strong>Confirm</strong> button next to the Shipment
      Dropdown. The invoice finalizes only after <strong>all shipments are resolved and confirmed</strong>.
    </p>
    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-24'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        In workflows with Finance review, the Confirm button may also assign the shipment to Finance when escalation criteria are met.
      </p>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Invoice Outcome Logic</h4>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Outcome</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Condition</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Ready for Payment', 'All shipments approved/resolved without rejection (including any required Finance approvals)'],
            ['Rejected', 'Invoice explicitly rejected manually'],
            ['Dispute', 'Triggered when at least one shipment has been rejected, after all shipments are confirmed'],
          ].map(([outcome, condition]) => (
            <tr key={outcome} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{outcome}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{condition}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Toolbar Actions (Top Right)</h4>
    <img src={imgInvoiceIssue04} alt='Toolbar action icons' className='mb-16 block' />
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Icon</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Action</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Invoice Report', 'Opens and downloads invoice PDF'],
            ['View Invoice Files', 'Opens invoice file(s); shows Duplicate badge if relevant'],
            ['Configured Rate Card', 'Opens and downloads rate card PDF'],
            ['Activity Log', 'Opens audit trail pop-up'],
            ['Attach Documents', 'Opens document upload panel'],
            ['Contact Freya', 'Opens escalation modal'],
          ].map(([icon, action]) => (
            <tr key={icon} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{icon}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Contact Freya (Escalation)</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>If you need support on a specific shipment:</p>
    <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>
        Click <strong>Contact Freya</strong> from the toolbar.
      </li>
      <li>The modal shows: Service Provider, Invoice Number(s), Shipment Number(s), and Current Status.</li>
      <li>Enter a description of the issue.</li>
      <li>
        Click <strong>Submit</strong>.
      </li>
    </ol>
    <p className='text-grey-10 mb-8 leading-relaxed'>After submission:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>
        A toast confirms: <em>"Shipment [No.] is now under review. Support Team will be in touch with you shortly. Issue SR-XXX has been created."</em>
      </li>
      <li>
        The shipment is reclassified as <strong>Under Review</strong> in the Shipment Dropdown.
      </li>
    </ul>
  </>
);

const HandlingDisputesContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Handling Disputes</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Overview</h4>
    <p className='text-grey-10 mb-24 leading-relaxed'>
      The Dispute process allows users to formally escalate invoices that contain rejected shipments after the review process.
    </p>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Requirements to Begin a Dispute</h4>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-16'>
      <li>All shipments on the invoice must already be resolved.</li>
      <li>
        At least one shipment must be in <strong>Rejected</strong> state.
      </li>
      <li>No shipments may be under Finance review (in workflows with Finance escalation).</li>
      <li>
        Once conditions are met, the <strong>Dispute</strong> button becomes available on the Action Details screen.
      </li>
    </ul>
    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-24'>
      <p className='text-grey-10 text-14 leading-relaxed'>
        <strong>⚠️ Note:</strong> In workflows with Finance review, only the primary reviewer role (e.g., Supply Chain) can initiate a dispute.
      </p>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-20'>Dispute Flow</h4>

    {/* Step 1 */}
    <div className='mb-24'>
      <p className='font-medium text-neutral-80 mb-8'>Step 1 – Contact Service Provider</p>
      <p className='text-grey-10 mb-12 leading-relaxed'>Review the Invoice Issue Summary, which includes:</p>
      <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-16'>
        <li>Categories of rejection (e.g., deviation not approved, NRCF rejected).</li>
        <li>Count of impacted shipments.</li>
        <li>Total Amount in Dispute.</li>
      </ul>
      <p className='text-grey-10 mb-8 leading-relaxed'>Choose one of the following actions:</p>
      <div className='overflow-x-auto mb-4'>
        <table className='w-full border-collapse text-14'>
          <thead>
            <tr className='bg-neutral-10'>
              <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Action</th>
              <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Accept Invoice', 'Accept with an expected credit note amount'],
              ['Reject Invoice', 'Cancel the invoice entirely'],
              ['Cancel Dispute', 'Return to Action Details; status = Dispute Canceled'],
            ].map(([action, description]) => (
              <tr key={action} className='even:bg-neutral-5'>
                <td className='border border-neutral-20 px-12 py-8 font-medium'>{action}</td>
                <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Step 2 */}
    <div className='mb-24'>
      <p className='font-medium text-neutral-80 mb-12'>Step 2 – Resolution</p>

      <p className='text-grey-10 mb-4 leading-relaxed'>
        <em>Accept Invoice:</em>
      </p>
      <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-12'>
        <li>Specify acceptance reason.</li>
        <li>Enter expected credit note amount.</li>
        <li>Optionally upload supporting documents.</li>
      </ul>

      <p className='text-grey-10 mb-4 leading-relaxed'>
        <em>Reject Invoice:</em>
      </p>
      <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-12'>
        <li>Provide rejection reason.</li>
        <li>Optionally upload supporting documents.</li>
      </ul>

      <p className='text-grey-10 mb-4 leading-relaxed'>
        <em>Cancel Dispute:</em>
      </p>
      <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-4'>
        <li>Provide cancellation reason.</li>
        <li>
          Returns to Action Details with status = <strong>Dispute Canceled</strong>.
        </li>
        <li>Previously rejected questionnaires reopen for further editing.</li>
      </ul>
    </div>

    {/* Step 3 */}
    <div className='mb-24'>
      <p className='font-medium text-neutral-80 mb-8'>Step 3 – Summary</p>
      <p className='text-grey-10 mb-8 leading-relaxed'>Review a recap of the dispute action before finalizing:</p>
      <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-4'>
        <li>Service Provider, Invoice Number, Shipments Count.</li>
        <li>Actions taken and notes.</li>
        <li>Uploaded supporting documents.</li>
      </ul>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>Dispute Outcomes</h4>
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Outcome</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Invoice Status</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Accepted Invoice', 'Ready for Payment'],
            ['Rejected Invoice', 'Rejected'],
            ['Canceled Dispute', 'Dispute Canceled (returns to Action Details)'],
          ].map(([outcome, status]) => (
            <tr key={outcome} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{outcome}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className='font-medium text-neutral-80 mb-8'>Cancel Dispute – Workflow with Finance Review</p>
    <p className='text-grey-10 mb-8 leading-relaxed'>When a dispute is canceled in a workflow with Finance escalation:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-24'>
      <li>If the only pending deviations are those previously rejected by Finance → the invoice is reassigned directly to Finance for re-review.</li>
      <li>
        If there are deviations that the primary reviewer also needs to accept → the invoice is reassigned to the primary reviewer first. After resolving, they
        confirm to send it to Finance (if applicable).
      </li>
    </ul>

    <p className='text-grey-10 mb-24 leading-relaxed'>
      All dispute actions are logged in the <strong>Activity Log</strong> with user, role, timestamp, comments, and uploaded files.
    </p>
  </>
);

const DeviationsContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Understanding Charge Deviations</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Overview</h4>
    <p className='text-grey-10 mb-24 leading-relaxed'>
      The Charge Deviations module highlights differences between invoice charges and the configured Rate Card charges. Deviations are categorized by type and
      assigned to the appropriate role based on the active audit workflow.
    </p>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-16'>Deviation Types</h4>

    <div className='space-y-20 mb-24'>
      <div>
        <p className='text-grey-10 mb-8 leading-relaxed'>
          <strong>1. Unit Price Difference</strong> — The invoice unit price is higher or different from the rate card unit price.
        </p>
        <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16'>
          <p className='text-grey-10 text-14 leading-relaxed'>
            <em>Example: Rate Card = USD 552/unit, Invoice = USD 702/unit → Deviation = USD 150/unit.</em>
          </p>
        </div>
      </div>

      <div>
        <p className='text-grey-10 mb-8 leading-relaxed'>
          <strong>2. Quantity Difference</strong> — The quantity billed on the invoice exceeds what the Rate Card allows.
        </p>
        <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16'>
          <p className='text-grey-10 text-14 leading-relaxed'>
            <em>Example: Rate Card allows 1 unit, Invoice bills 2 units → Deviation = 1 excess unit.</em>
          </p>
        </div>
      </div>

      <div>
        <p className='text-grey-10 leading-relaxed'>
          <strong>3. Unknown Charge</strong> — A charge on the invoice has no match in the Rate Card. The assigned role must classify it.
        </p>
      </div>

      <div>
        <p className='text-grey-10 leading-relaxed'>
          <strong>4. Double Charge</strong> — A charge appears to be billed twice across invoices. The system flags this by matching the charge name, leg, and
          shipment number against existing invoices.
        </p>
      </div>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-8'>Decision Options by Role</h4>
    <p className='text-grey-10 mb-24 leading-relaxed'>
      The decisions available to you depend on the deviation type and your role within the configured audit workflow. Refer to the{' '}
      <strong>Audit Workflows</strong> topic for the specific decision options available to each role.
    </p>
  </>
);

const NRCFContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Handling "No Rate Card Found" (NRCF)</h3>

    <h4 className='text-18 font-medium text-neutral-80 mb-8'>What is NRCF?</h4>
    <p className='text-grey-10 mb-12 leading-relaxed'>
      NRCF (No Rate Card Found) means FREYA couldn't find a matching rate card for a shipment. Without a rate card, FREYA can't validate whether the charges are
      correct – so it flags the invoice for Procurement to decide what to do.
    </p>

    <p className='font-medium text-neutral-80 mb-8'>Why NRCF happens:</p>
    <ul className='list-disc list-inside space-y-4 text-grey-10 pl-4 mb-12'>
      <li>New service lane that hasn't been set up yet</li>
      <li>New vendor without configured rates</li>
      <li>Shipment details don't match any existing rate card criteria</li>
      <li>Rate card expired or hasn't been renewed</li>
    </ul>
    <p className='text-grey-10 mb-16 leading-relaxed'>
      <strong>Your job:</strong> Decide how to handle the shipment – either set up proper rates for the future, process it as a one-time exception, or reject
      it.
    </p>
    <img src={imgNRCF01} alt='Shipment dropdown showing Review NRCF button' className='mb-24 block w-[320px]' />

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-20'>Your Three Options</h4>

    {/* Option A */}
    <div className='mb-32'>
      <p className='font-medium text-neutral-80 mb-4'>
        Option A: Set Up a New Service <span className='font-normal text-grey-10'>— Best for: Ongoing services that will repeat</span>
      </p>
      <img src={imgNRCF02} alt='Set Up a New Service option card' className='mb-12 block' />
      <p className='text-grey-10 mb-16 leading-relaxed'>This creates a proper rate card entry that will be used for future invoices.</p>

      <p className='text-grey-10 mb-8 leading-relaxed'>
        <em>Using an existing service as a template:</em>
      </p>
      <img src={imgNRCF03} alt='Copy Charges from an Existing Shipping Service' className='mb-12 block w-[750px]' />
      <ol className='list-decimal list-inside space-y-16 text-grey-10 pl-4 mb-20'>
        <li>Browse the list of existing shipping services</li>
        <li>Select one that's similar</li>
        <li>
          Review the charges that will be copied (grouped by type: Declaration, Shipment, Weight, Container, Percentage)
          <br />
          <img src={imgNRCF04} alt='Charges Details panel' className='mt-12 block w-[750px]' />
        </li>
        <li>
          Click <strong>Copy Charges</strong>
        </li>
        <li>
          Edit any charges that need adjustment
          <br />
          <img src={imgNRCF05} alt='Edit charges screen' className='mt-12 block w-[750px]' />
        </li>
        <li>Confirm to create the new service</li>
      </ol>

      <p className='text-grey-10 mb-8 leading-relaxed'>
        <em>Creating from scratch:</em>
      </p>
      <ol className='list-decimal list-inside space-y-16 text-grey-10 pl-4 mb-8'>
        <li>
          Skip the existing services
          <br />
          <img src={imgNRCF06} alt='Skip button to bypass existing services' className='mt-12 block w-[320px]' />
        </li>
        <li>
          Manually enter all charge details:
          <br />
          <img src={imgNRCF07} alt='Blank charge entry form' className='mt-12 block w-[750px]' />
          <ul className='list-disc list-inside space-y-2 mt-8 pl-4'>
            <li>Charge Name</li>
            <li>Category</li>
            <li>Leg Type</li>
            <li>Unit Price</li>
            <li>Currency</li>
            <li>Charge Unit</li>
            <li>Min/Max Units</li>
            <li>Validity Period</li>
          </ul>
        </li>
        <li>Review for any mismatches (shown in red text)</li>
        <li>Confirm to create the new service</li>
      </ol>
    </div>

    {/* Option B */}
    <div className='mb-32'>
      <p className='font-medium text-neutral-80 mb-4'>
        Option B: Accept as Spot Shipment <span className='font-normal text-grey-10'>— Best for: One-time or unusual shipments that won't repeat</span>
      </p>
      <img src={imgNRCF08} alt='Accept as Spot Shipment option card' className='mb-12 block' />
      <p className='text-grey-10 mb-16 leading-relaxed'>This processes the invoice without creating a permanent rate card entry.</p>

      <p className='font-medium text-neutral-80 mb-8'>What you'll do:</p>
      <ol className='list-decimal list-inside space-y-16 text-grey-10 pl-4 mb-16'>
        <li>Review the charges from the invoice</li>
        <li>
          Categorize each charge (assign charge category and leg type)
          <br />
          <img src={imgNRCF09} alt='Categorize spot shipment charges table' className='mt-12 block w-[750px]' />
        </li>
        <li>
          You can also accept or reject the charge using the toggle (provide reason if you select reject)
          <br />
          <img src={imgNRCF10} alt='Decision toggle for accepting or rejecting a charge' className='mt-12 block w-[200px]' />
        </li>
        <li>Confirm to process</li>
      </ol>
      <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16'>
        <p className='text-grey-10 text-14 leading-relaxed'>
          <strong>⚠️ Note:</strong> Spot shipment charges are NOT saved to your rate cards. The next time a similar shipment arrives, FREYA will flag it as NRCF
          again.
        </p>
      </div>
    </div>

    {/* Option C */}
    <div className='mb-32'>
      <p className='font-medium text-neutral-80 mb-4'>
        Option C: Reject the Shipment <span className='font-normal text-grey-10'>— Best for: Shipments that shouldn't have been billed to you</span>
      </p>
      <p className='text-grey-10 mb-12 leading-relaxed'>This rejects all charges for the shipment.</p>
      <p className='font-medium text-neutral-80 mb-8'>What you'll do:</p>
      <ol className='list-decimal list-inside space-y-4 text-grey-10 pl-4 mb-12'>
        <li>Select "Reject Shipment"</li>
        <li>Provide a rejection reason</li>
        <li>Confirm</li>
      </ol>
      <p className='text-grey-10 leading-relaxed'>The invoice remains flagged, and you can proceed with the dispute process.</p>
    </div>

    <hr className='border-neutral-20 mb-24' />
    <h4 className='text-18 font-medium text-neutral-80 mb-12'>NRCF Tips</h4>
    <div className='space-y-12'>
      <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16'>
        <p className='text-grey-10 text-14 leading-relaxed'>
          <strong>💡 Save your progress:</strong> Click <strong>Save to Draft &amp; Close</strong> if you need to step away. Your work will be saved and you can
          return later.
        </p>
      </div>
      <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16'>
        <p className='text-grey-10 text-14 leading-relaxed'>
          <strong>💡 Red text warnings:</strong> If you see values highlighted in red, they indicate a mismatch between what you've entered and the shipment
          details (like wrong leg type or trade lane). Review and correct these before confirming.
        </p>
      </div>
      <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16'>
        <p className='text-grey-10 text-14 leading-relaxed'>
          <strong>💡 Need help?</strong> Click <strong>Contact Freya</strong> to escalate to the support team.
        </p>
      </div>
    </div>
  </>
);

const ReportAnalyticContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>FREYA Report & Analytics</h3>

    {/* What is Reports & Analytics */}
    <p className='mb-20'>
      Reports & Analytics is your data hub for understanding invoice performance, tracking spending, and generating insights. You can create custom views of
      your data, visualize trends, and export information for further analysis or sharing with stakeholders.
    </p>

    <h4 className='text-18 font-semibold mb-12'>What you can do:</h4>
    <ul className='list-disc pl-24 mb-24 space-y-8'>
      <li>View key metrics like total spend, shipment counts, and deviations</li>
      <li>Create charts to visualize trends over time</li>
      <li>Filter data by provider, trade lane, date range, and more</li>
      <li>Export data to Excel or CSV</li>
      <li>Save custom reports for quick access later</li>
      <li>Send approved invoices to your AP team</li>
    </ul>

    {/* Getting Started */}
    <h4 className='text-18 font-semibold mb-12'>Getting Started</h4>
    <ol className='list-decimal pl-24 mb-24 space-y-8'>
      <li>
        Click <strong>Reports & Analytics</strong> in the sidebar
      </li>
      <li>
        Choose to:
        <ul className='list-disc pl-24 mt-8 space-y-6'>
          <li>
            Open a <strong>saved report</strong> from the list
          </li>
          <li>
            Click <strong>Start New</strong> to create a fresh report
          </li>
        </ul>
      </li>
    </ol>

    {/* Two Ways to View */}
    <h4 className='text-18 font-semibold mb-16'>Two Ways to View Your Data</h4>
    <img src={imgReport01} alt='Chart and Table view toggle' className='mb-24 rounded-8 w-[200px]' />

    <h4 className='text-18 font-semibold mb-4'>Chart Mode – Visual Analysis</h4>
    <p className='mb-16 text-neutral-60 text-16'>Best for: Spotting trends, presentations, quick insights</p>
    <img src={imgReport02} alt='Chart visualization' className='mb-16 rounded-8  w-[750px]' />
    <ul className='list-disc pl-24 mb-24 space-y-8'>
      <li>See metrics displayed as graphs and charts</li>
      <li>Compare performance over time</li>
      <li>Analyze by trade lane or service provider</li>
      <li>Identify patterns and correlations</li>
    </ul>

    <p className='mb-12 font-medium'>What you can visualize:</p>
    <div className='overflow-x-auto mb-16'>
      <table className='w-full border-collapse text-16'>
        <thead>
          <tr className='bg-neutral-5'>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>Tab</th>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>What It Shows</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Trends</td>
            <td className='border border-neutral-20 px-16 py-10'>Metrics over time (daily, weekly, monthly)</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Trade Lane</td>
            <td className='border border-neutral-20 px-16 py-10'>Breakdown by shipping routes or countries</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Shipment Details</td>
            <td className='border border-neutral-20 px-16 py-10'>Operational shipment-level data</td>
          </tr>
        </tbody>
      </table>
    </div>
    <img src={imgReport03} alt='Chart tabs navigation' className='mb-24 rounded-8  w-[750px]' />

    <h4 className='text-18 font-semibold mb-4'>Table Mode – Detailed Data</h4>
    <p className='mb-16 text-neutral-60 text-16'>Best for: Finding specific records, exporting data, detailed analysis</p>
    <ul className='list-disc pl-24 mb-32 space-y-8'>
      <li>See individual records in rows</li>
      <li>Filter and sort by any column</li>
      <li>Cross-reference across different data types</li>
      <li>Export exactly what you need</li>
    </ul>

    {/* Customizing Metrics */}
    <h4 className='text-18 font-semibold mb-12'>Customizing Your Metrics</h4>
    <p className='mb-16'>Display up to 4 metrics at a time to focus on what matters most.</p>
    <img src={imgReport04} alt='Metrics bar' className='mb-16 rounded-8  w-[900px]' />
    <ol className='list-decimal pl-24 mb-16 space-y-8'>
      <li>
        Click <strong>Metrics</strong> on the right panel
      </li>
      <li>
        Select from available metrics:
        <ul className='list-disc pl-24 mt-8 space-y-6'>
          <li>Invoices (count and amount)</li>
          <li>Shipments</li>
          <li>Charges</li>
          <li>CO₂e Emissions</li>
          <li>Volume</li>
          <li>Chargeable Weight</li>
          <li>Gross Weight</li>
          <li>Total Net Deviation Amount</li>
        </ul>
      </li>
      <li>
        Click <strong>Apply</strong>
      </li>
      <li>
        Click <strong>Reset</strong> to restore defaults
      </li>
    </ol>
    <img src={imgReport05} alt='Metrics selection panel' className='mb-24 rounded-8 max-w-full' />

    {/* Dual Display */}
    <h4 className='text-18 font-semibold mb-12'>Understanding the Dual Display</h4>
    <p className='mb-16'>Each metric shows two numbers:</p>
    <img src={imgReport06} alt='Dual display metric card' className='mb-16 rounded-8 max-w-full' />
    <ul className='list-disc pl-24 mb-12 space-y-8'>
      <li>
        <strong>Total</strong> – The full dataset (with global filters applied)
      </li>
      <li>
        <strong>X Table Filter</strong> – Just the records in your current filtered table view
      </li>
    </ul>
    <p className='mb-32'>This helps you see both the big picture and your specific selection.</p>

    {/* Filtering */}
    <h4 className='text-18 font-semibold mb-12'>Filtering Your Data</h4>
    <p className='mb-12'>Narrow down to exactly what you need:</p>

    <h4 className='text-16 font-semibold mb-8'>Date Filters:</h4>
    <ul className='list-disc pl-24 mb-16 space-y-8'>
      <li>Year, Quarter, Month, Week, or specific Day</li>
      <li>Based on pickup or delivery dates</li>
    </ul>

    <h4 className='text-16 font-semibold mb-8'>Additional Filters:</h4>
    <div className='overflow-x-auto mb-16'>
      <table className='w-full border-collapse text-16'>
        <thead>
          <tr className='bg-neutral-5'>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>Filter</th>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>What It Filters</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>Trade Lane</td>
            <td className='border border-neutral-20 px-16 py-10'>Specific shipping routes</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>Country / City Hub</td>
            <td className='border border-neutral-20 px-16 py-10'>Geographic locations</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>Service Provider</td>
            <td className='border border-neutral-20 px-16 py-10'>Specific carriers</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>Modality</td>
            <td className='border border-neutral-20 px-16 py-10'>Transport type (Air, Sea, Road, etc.)</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>Invoice No. / Shipment No.</td>
            <td className='border border-neutral-20 px-16 py-10'>Specific records</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>Billing Reference</td>
            <td className='border border-neutral-20 px-16 py-10'>Your internal references</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>Leg Type / Charge Type</td>
            <td className='border border-neutral-20 px-16 py-10'>Journey segments or charge categories</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>Invoice Status</td>
            <td className='border border-neutral-20 px-16 py-10'>Current state of invoices</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className='mb-32'>
      Click <strong>Reset to Default</strong> to clear all filters.
    </p>

    {/* Working with Data Tables */}
    <h4 className='text-18 font-semibold mb-12'>Working with Data Tables</h4>
    <p className='mb-16'>Three tabs organize your data:</p>

    <div className='mb-16'>
      <p className='mb-8'>
        <strong>Shipment Tab</strong> – Shipment-level details including:
      </p>
      <ul className='list-disc pl-24 mb-16 space-y-8'>
        <li>Shipment number, origin, destination</li>
        <li>Pickup and delivery dates</li>
        <li>Service provider and modality</li>
        <li>CO₂ emissions, weights, volume</li>
        <li>Status and reason</li>
      </ul>
    </div>

    <div className='mb-16'>
      <p className='mb-8'>
        <strong>Invoice Tab</strong> – Invoice-level details including:
      </p>
      <ul className='list-disc pl-24 mb-16 space-y-8'>
        <li>Invoice number and status</li>
        <li>Service provider and modality</li>
        <li>Billed entity and FAP reference</li>
        <li>Total amount and charge count</li>
        <li>Review stage and reason</li>
      </ul>
    </div>

    <div className='mb-24'>
      <p className='mb-8'>
        <strong>Charges Tab</strong> – Individual charge line details including:
      </p>
      <ul className='list-disc pl-24 mb-16 space-y-8'>
        <li>Charge name and category</li>
        <li>Leg information</li>
        <li>Amounts (invoiced, original, reference)</li>
        <li>Quantity, unit price, exchange rate</li>
        <li>Status and reason</li>
      </ul>
    </div>

    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-32'>
      <p className='text-14 m-0'>
        💡 <strong>Cross-Tab Filtering:</strong> When you filter in one tab, the filter automatically applies to all tabs. A "Column Filtered" pill appears to
        remind you. Click the X to clear.
      </p>
    </div>

    {/* Saving and Exporting */}
    <h4 className='text-18 font-semibold mb-12'>Saving and Exporting</h4>

    <h4 className='text-16 font-semibold mb-12'>Save Your Report</h4>
    <img src={imgReport07} alt='Save report dialog' className='mb-16 rounded-8 max-w-full' />
    <div className='overflow-x-auto mb-24'>
      <table className='w-full border-collapse text-16'>
        <thead>
          <tr className='bg-neutral-5'>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>Option</th>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>What It Does</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Replace Original</td>
            <td className='border border-neutral-20 px-16 py-10'>Updates an existing saved report with current settings</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Save as New</td>
            <td className='border border-neutral-20 px-16 py-10'>Creates a new saved report (provide name and description)</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h4 className='text-16 font-semibold mb-12'>Export Data</h4>
    <p className='mb-12'>Download your current view:</p>
    <ul className='list-disc pl-24 mb-12 space-y-8'>
      <li>
        <strong>CSV</strong> – For spreadsheet applications, data analysis
      </li>
      <li>
        <strong>XLSX</strong> – For Excel with formatting preserved
      </li>
    </ul>
    <p className='mb-32'>Exports respect your current filters, active tab, and visible columns.</p>

    {/* Sending to AP */}
    <h4 className='text-18 font-semibold mb-12'>Sending Invoices to AP Team</h4>
    <p className='mb-16'>When invoices are ready for payment:</p>
    <ol className='list-decimal pl-24 mb-16 space-y-8'>
      <li>
        Go to the <strong>Invoice Tab</strong>
      </li>
      <li>Select the invoices you want to send (check the boxes)</li>
      <li>
        Click <strong>Send to AP Team</strong>
      </li>
      <li>Assign a FAP Reference number (new or existing)</li>
      <li>Confirm</li>
    </ol>
    <img src={imgReport08} alt='Send to AP team confirmation' className='mb-16 rounded-8  w-[450px]' />
    <p className='mb-8'>
      You'll see a confirmation: <em>"X invoices sent to AP Team. FAP Ref: AP2023-05"</em>
    </p>
  </>
);

const PerformanceAssessmentContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>FREYA Performance Assessment</h3>

    {/* What is Performance Assessment */}
    <p className='mb-20'>
      Performance Assessment is your dashboard for measuring ROI and tracking how much FREYA is saving your organization. It shows you the financial impact of
      invoice auditing across all service providers – before and after FREYA's analysis.
    </p>

    <h4 className='text-18 font-semibold mb-12'>What you'll learn:</h4>
    <ul className='list-disc pl-24 mb-32 space-y-8'>
      <li>Total savings achieved through auditing</li>
      <li>Deviation amounts caught before payment</li>
      <li>Performance comparison across service providers</li>
      <li>Month-by-month trends</li>
    </ul>

    {/* Understanding Savings Metrics */}
    <h4 className='text-18 font-semibold mb-12'>Understanding the Savings Metrics</h4>
    <p className='mb-16'>At the top, you'll see aggregate metrics across all providers:</p>
    <div className='overflow-x-auto mb-16'>
      <table className='w-full border-collapse text-16'>
        <thead>
          <tr className='bg-neutral-5'>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>Metric</th>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>What It Shows</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Amount</td>
            <td className='border border-neutral-20 px-16 py-10'>Dollar values: Before Audit → After Audit → Deviations → Savings</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Shipments</td>
            <td className='border border-neutral-20 px-16 py-10'>Shipment counts through the audit process</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Invoices</td>
            <td className='border border-neutral-20 px-16 py-10'>Invoice counts through the audit process</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10 font-semibold'>Charge Lines</td>
            <td className='border border-neutral-20 px-16 py-10'>Individual charge line counts</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h4 className='text-16 font-semibold mb-8'>How to read the percentages:</h4>
    <ul className='list-disc pl-24 mb-32 space-y-8'>
      <li>"After Audit %" shows reduction from Before Audit</li>
      <li>"Deviation %" shows deviation portion of Before Audit</li>
      <li>"Savings %" shows savings portion of deviation amount</li>
    </ul>

    {/* Drilling Down */}
    <h4 className='text-18 font-semibold mb-12'>Drilling Down by Service Provider</h4>
    <p className='mb-12'>Each service provider has an expandable section showing:</p>
    <ul className='list-disc pl-24 mb-32 space-y-8'>
      <li>Same metrics, filtered to just that provider</li>
      <li>
        Quick links to their <strong>Audit Exceptions</strong> and <strong>Reports & Analytics</strong>
      </li>
      <li>Monthly overview table</li>
    </ul>

    {/* Monthly Overview Table */}
    <h4 className='text-18 font-semibold mb-12'>Monthly Overview Table</h4>
    <p className='mb-16'>Each provider shows 12 months of data per page:</p>
    <div className='overflow-x-auto mb-16'>
      <table className='w-full border-collapse text-16'>
        <thead>
          <tr className='bg-neutral-5'>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>Column</th>
            <th className='border border-neutral-20 px-16 py-10 text-left font-semibold'>What It Shows</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>Month</td>
            <td className='border border-neutral-20 px-16 py-10'>Calendar month</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>Total Amount</td>
            <td className='border border-neutral-20 px-16 py-10'>Total invoiced amount</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>Deviations</td>
            <td className='border border-neutral-20 px-16 py-10'>Deviation amounts found</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>Savings</td>
            <td className='border border-neutral-20 px-16 py-10'>Money saved through auditing</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>No. Shipments</td>
            <td className='border border-neutral-20 px-16 py-10'>Shipment count</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>No. Invoices</td>
            <td className='border border-neutral-20 px-16 py-10'>Invoice count</td>
          </tr>
          <tr>
            <td className='border border-neutral-20 px-16 py-10'>No. Charge Lines</td>
            <td className='border border-neutral-20 px-16 py-10'>Charge line count</td>
          </tr>
          <tr className='bg-neutral-5'>
            <td className='border border-neutral-20 px-16 py-10'>Audited %</td>
            <td className='border border-neutral-20 px-16 py-10'>Percentage of invoices that were audited</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-8'>
      <p className='text-14 m-0'>
        💡 <strong>Export:</strong> Click the download icon to export the monthly data as CSV or Excel.
      </p>
    </div>
  </>
);

const RateCardManagerContent = () => (
  <>
    <h3 className='text-22 font-bold text-neutral-80 mb-20'>Rate Card Manager Feature</h3>

    {/* What is Rate Card Manager */}
    <p className='mb-20'>
      Rate Card Manager is where you set up and maintain your contracted rates with service providers. These rates are the "source of truth" that FREYA uses to
      validate invoices. When an invoice arrives, FREYA compares each charge against the rates you've configured here.
    </p>

    <h4 className='text-18 font-semibold mb-12'>Why it matters:</h4>
    <ul className='list-disc pl-24 mb-24 space-y-8'>
      <li>Accurate rate cards = accurate invoice validation</li>
      <li>Outdated rates = false flags or missed overcharges</li>
      <li>Missing rates = NRCF issues</li>
    </ul>

    <h4 className='text-18 font-semibold mb-12'>How it's organized:</h4>
    <div className='bg-neutral-90 text-neutral-5 rounded-8 p-16 mb-24 font-mono text-14 leading-loose'>
      <div>1 &nbsp; Service Provider</div>
      <div>2 &nbsp; &nbsp; └── Shipping Service (e.g., "Air Freight - Singapore to Tokyo")</div>
      <div>3 &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; └── Validity Period (e.g., "Jan 1 - Dec 31, 2024")</div>
      <div>4 &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; └── Charges (the actual rates)</div>
    </div>

    {/* Getting There */}
    <h4 className='text-18 font-semibold mb-12'>Getting There</h4>
    <p className='mb-8 font-medium'>From the main menu:</p>
    <ol className='list-decimal pl-24 mb-12 space-y-8'>
      <li>
        Click <strong>Rate Card Manager</strong>
      </li>
      <li>
        Select a <strong>Service Provider</strong> or <strong>select Modality</strong>
      </li>
      <li>You'll see all shipping services for that provider</li>
    </ol>
    <p className='mb-32'>
      <strong>From Action Details:</strong> Click "View in Rate Card Manager" to jump directly to the relevant service.
    </p>

    {/* Managing Shipping Services */}
    <h4 className='text-18 font-semibold mb-12'>Managing Shipping Services</h4>
    <img src={imgRCM01} alt='Rate Card Management services table' className='mb-16 rounded-8 max-w-full w-[750px]' />
    <p className='mb-12 mt-30'>
      <strong>The Services Table</strong>The top section shows all shipping services for the selected provider:
    </p>
    <img src={imgRCM02} alt='Services table with charge details panel' className='mb-16 rounded-8 max-w-full w-[750px]' />

    <p className='mb-12'>Each row includes:</p>
    <ul className='list-disc pl-24 mb-24 space-y-8'>
      <li>Modality (Air, Road, Sea, etc.)</li>
      <li>Trade Lane (origin → destination)</li>
      <li>Shipper / Consignee codes</li>
      <li>Billed Entity</li>
      <li>Service Type, Carrier, Incoterm</li>
      <li>Lead Time</li>
      <li>Created date</li>
    </ul>

    <h4 className='text-16 font-semibold mb-12'>Adding a New Service:</h4>
    <img src={imgRCM03} alt='Add New button' className='mb-16 rounded-8 max-w-full' />
    <ol className='list-decimal pl-24 mb-12 space-y-8'>
      <li>
        Click <strong>Add New</strong>
      </li>
      <li>
        A blank row appears
        <div className='mt-12'>
          <img src={imgRCM04} alt='Blank new service row' className='rounded-8 max-w-full' />
        </div>
      </li>
      <li>Fill in the details</li>
      <li>Save</li>
    </ol>
    <p className='mb-32'>
      <strong>Viewing Service Details:</strong> Click any service row to open the Charge Details panel.
    </p>

    {/* Managing Validity Periods */}
    <h4 className='text-18 font-semibold mb-12'>Managing Validity Periods</h4>
    <p className='mb-16'>Each shipping service can have multiple validity periods – different rates for different time ranges.</p>
    <img src={imgRCM05} alt='Validity period dropdown' className='mb-24 rounded-8 w-[250px]' />

    <h4 className='text-16 font-semibold mb-12'>Creating a New Validity Period:</h4>

    <p className='mb-8 font-medium italic'>Option 1: Copy from existing (faster)</p>
    <img src={imgRCM06} alt='Create Validity Period - copy from existing' className='mb-16 rounded-8 max-w-full w-[520px]' />
    <ol className='list-decimal pl-24 mb-24 space-y-8'>
      <li>Select a source period from the dropdown</li>
      <li>
        Review the charges that will be copied:
        <ul className='list-disc pl-24 mt-8 space-y-6'>
          <li>Declaration Charges</li>
          <li>Shipment Charges</li>
          <li>Weight Charges</li>
          <li>Container Charges</li>
          <li>Percentage Charges</li>
        </ul>
      </li>
      <li>Confirm to create</li>
      <li>Edit individual charges as needed</li>
    </ol>

    <p className='mb-8 font-medium italic'>Option 2: Create blank (for new services)</p>
    <img src={imgRCM07} alt='Create Validity Period - blank' className='mb-16 rounded-8 w-[520px]  ' />
    <ol className='list-decimal pl-24 mb-24 space-y-8'>
      <li>Select "Create Blank Period"</li>
      <li>Enter start and end dates</li>
      <li>Add charges manually</li>
    </ol>

    <p className='mb-12'>
      <strong>Editing a Validity Period:</strong> Click <strong>Update Validity Period</strong> to change the dates.
    </p>
    <img src={imgRCM08} alt='Update Validity Period dialog' className='mb-16 rounded-8  w-[520px]' />
    <div className='bg-neutral-5 border-l-4 border-primary rounded-4 p-16 mb-16'>
      <p className='text-14 m-0'>
        <strong>⚠️ Note:</strong> Changing dates doesn't affect invoices already validated. Historical records stay the same.
      </p>
    </div>

    <p className='mb-32'>
      <strong>Deleting a Validity Period:</strong> You can only delete a period if no invoices have been validated against it. If deletion is available, you'll
      see the option with a note: "No invoices are attached to this period."
    </p>

    {/* Managing Charges */}
    <h4 className='text-18 font-semibold mb-12'>Managing Charges</h4>
    <p className='mb-12'>
      <strong>Editing Charges:</strong> Click any cell to edit directly. Changes save automatically.
    </p>
    <p className='mb-12'>
      <strong>Adding Charges:</strong> Click <strong>Add New</strong> in the charges table and fill in:
    </p>
    <img src={imgRCM09} alt='New charge row in charges table' className='mb-16 rounded-8 max-w-full' />
    <ul className='list-disc pl-24 mb-24 space-y-8'>
      <li>Charge Name</li>
      <li>Category</li>
      <li>Leg Type</li>
      <li>Unit Price</li>
      <li>Currency</li>
      <li>Charge Unit</li>
      <li>And other relevant fields</li>
    </ul>

    <h4 className='text-16 font-semibold mb-12'>Bulk Copy-Paste:</h4>
    <p className='mb-12'>For fast data entry:</p>
    <ol className='list-decimal pl-24 mb-24 space-y-8'>
      <li>Select rows to copy</li>
      <li>
        Press <code className='bg-neutral-10 px-8 py-2 rounded-4 font-mono text-14'>Ctrl/Cmd + C</code>
      </li>
      <li>Navigate to the destination (same or different period)</li>
      <li>
        Press <code className='bg-neutral-10 px-8 py-2 rounded-4 font-mono text-14'>Ctrl/Cmd + V</code>
      </li>
    </ol>
    <p className='mb-32'>
      <strong>Deleting Charges:</strong> Select rows and click the Delete button.
    </p>

    {/* Rate Card Discounts */}
    <h4 className='text-18 font-semibold mb-12'>Rate Card Discounts</h4>
    <p className='mb-16'>
      Some service providers have negotiated discounts that apply on top of standard rate card charges. When discounts are configured for a shipping service,
      you'll see a <strong>Rate Card Discount</strong> tab next to the <strong>Charge Details</strong> tab when viewing a service's details.
    </p>

    <h4 className='text-16 font-semibold mb-12'>Accessing Discounts:</h4>
    <ol className='list-decimal pl-24 mb-24 space-y-8'>
      <li>
        Open <strong>Rate Card Manager</strong> and select a Service Provider.
      </li>
      <li>Click on a Shipping Service row to open its details.</li>
      <li>
        Switch to the <strong>Rate Card Discount</strong> tab.
      </li>
      <li>
        Select a <strong>Validity Period</strong> from the dropdown to view discounts for that period.
      </li>
    </ol>

    <h4 className='text-16 font-semibold mb-12'>Discount Table Fields:</h4>
    <div className='mb-24 overflow-x-auto'>
      <table className='w-full border-collapse text-14'>
        <thead>
          <tr className='bg-neutral-10'>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Field</th>
            <th className='border border-neutral-20 px-12 py-8 text-left font-medium text-neutral-80'>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Discount Name', 'Name of the discount (e.g., "Incentive Discount")'],
            ['Eligible Charge Domain', 'The charge category this discount applies to'],
            ['Eligible Sub Domain', 'Specific sub-category within the charge domain (if applicable)'],
            ['Rate', 'The discount rate or amount'],
            ['Start Date', 'When the discount becomes effective'],
            ['End Date', 'When the discount expires'],
            ['Created On', 'Date the discount was added'],
          ].map(([field, description]) => (
            <tr key={field} className='even:bg-neutral-5'>
              <td className='border border-neutral-20 px-12 py-8 font-medium'>{field}</td>
              <td className='border border-neutral-20 px-12 py-8 text-grey-10'>{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h4 className='text-16 font-semibold mb-12'>Adding a Discount:</h4>
    <ol className='list-decimal pl-24 mb-24 space-y-8'>
      <li>
        Click <strong>Add New Discount</strong>.
      </li>
      <li>Fill in the discount details (name, eligible charge domain/sub domain, rate, start and end dates).</li>
      <li>Save.</li>
    </ol>

    <p className='mb-12'>
      <strong>Editing a Discount:</strong> Click the edit icon on any discount row to modify its values inline.
    </p>
    <p className='mb-32'>
      <strong>Deleting a Discount:</strong> Click the delete icon on a discount row and confirm the deletion.
    </p>

    {/* Finding What You Need */}
    <h4 className='text-18 font-semibold mb-12'>Finding What You Need</h4>
    <p className='mb-12'>
      <strong>Column Filters:</strong> Click the filter icon in any column header:
    </p>
    <ul className='list-disc pl-24 mb-16 space-y-8'>
      <li>See all unique values</li>
      <li>Check/uncheck to show/hide</li>
      <li>Search within values</li>
      <li>Sort A–Z or Z–A</li>
    </ul>
    <p className='mb-24'>
      Green icon + counter (e.g., <code className='bg-neutral-10 px-8 py-2 rounded-4 font-mono text-14'>2/5</code>) = filter active
    </p>
    <p className='mb-8'>
      <strong>Pagination:</strong> Choose to display 100, 500, or 1000 rows per page.
    </p>
  </>
);

const TOPIC_CONTENT: Record<string, React.FC> = {
  'Getting Started': GettingStartedContent,
  'Invoice Upload': InvoiceUploadContent,
  'Reviewing Audit Exceptions': ReviewingInvoicesContent,
  'Audit Workflows': AuditWorkflowsContent,
  'Resolving Invoice Issues': ResolvingInvoiceIssuesContent,
  'Understanding Charge Deviations': DeviationsContent,
  'Handling Disputes': HandlingDisputesContent,
  'Handling "No Rate Card Found" (NRCF)': NRCFContent,
  'FREYA Report & Analytics': ReportAnalyticContent,
  'FREYA Performance Assessment': PerformanceAssessmentContent,
  'Rate Card Manager Feature': RateCardManagerContent,
};

export const HelpContent: React.FC<HelpContentProps> = ({ activeTopic = 'Getting Started' }) => {
  const ContentComponent = TOPIC_CONTENT[activeTopic] || GettingStartedContent;
  const [showScrollTop, setShowScrollTop] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);

  const scrollToTop = useCallback(() => {
    if (scrollParentRef.current) {
      scrollParentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    // Find the scroll parent on mount
    scrollParentRef.current = getScrollParent(containerRef.current);
    const scrollContainer = scrollParentRef.current;

    if (!scrollContainer) return;

    const handleScroll = () => {
      setShowScrollTop(scrollContainer.scrollTop > 100);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeTopic]);

  return (
    <div ref={containerRef} className='flex-grow p-48 relative border-l border-neutral-20 overflow-y-auto'>
      <div className='max-w-none text-grey-10 text-16 leading-relaxed'>
        <ContentComponent />
      </div>

      <div
        className={`fixed bottom-[5%] right-[2%] transition-all duration-300 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div className='bg-white rounded-full shadow-lg border border-neutral-20 hover:shadow-xl transition-shadow'>
          <IconButton onClick={scrollToTop} size='large'>
            <ArrowUpward className='text-primary' />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
